/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件詳細でデザイン成果物フォルダを種別ごとに確認し、選んだ種別のフォルダを Google Drive に作る
 * @graph-connects useDeliverableFolders [calls] 種別ごとの有無の取得（欄を開いたときだけ）
 * @graph-connects useCreateDeliverableFolders [calls] 成果物フォルダの作成
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreateDeliverableFoldersResponse } from '@cortex/domain-project-intake-contract';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import {
  useCreateDeliverableFolders,
  useDeliverableFolders,
} from '../../../hooks/useDeliverableFolders.js';

/**
 * 作成結果のうち、利用者に知らせる必要があるもの（作れなかった種別と、Slack に投稿できなかった理由）。
 * 作れた種別は一覧のリンクに変わるので出さない。
 * @graph-connects none
 */
export function creationNotices(result: CreateDeliverableFoldersResponse | undefined): string[] {
  if (result === undefined) return [];
  const failed = result.folders
    .filter((folder) => folder.outcome === 'failed')
    .map((folder) => `${folder.label}: ${folder.message ?? ''}`);
  const slack =
    result.slack.status === 'no_channel' || result.slack.status === 'failed'
      ? [result.slack.message ?? '']
      : [];
  return [...failed, ...slack];
}

/**
 * 成果物フォルダの欄。開いたときだけ一覧を取る。権限・種別の判定は API に任せ、メッセージをそのまま出す。
 * @graph-connects useDeliverableFolders [calls] 種別ごとの有無の取得
 * @graph-connects useCreateDeliverableFolders [calls] 成果物フォルダの作成
 */
export function DeliverableFoldersSection({ pjKey }: { pjKey: string }): ReactNode {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const query = useDeliverableFolders(pjKey, { enabled: isOpen });
  const mutation = useCreateDeliverableFolders(pjKey);
  const notices = creationNotices(mutation.data);

  const toggle = (id: string, checked: boolean) => {
    setSelected((current) =>
      checked ? [...current, id] : current.filter((value) => value !== id),
    );
  };

  return (
    <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('projectDetail.deliverableFolders.title')}
        </h2>
        <Button
          size="sm"
          variant="ghost"
          aria-expanded={isOpen}
          onClick={() => {
            setIsOpen((current) => !current);
          }}
        >
          {isOpen
            ? t('projectDetail.deliverableFolders.close')
            : t('projectDetail.deliverableFolders.open')}
        </Button>
      </div>

      {isOpen && query.isPending && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">{t('spec.loading')}</p>
      )}
      {isOpen && query.isError && (
        <p role="alert" className="mt-2 text-xs text-[var(--color-error)]">
          {query.error instanceof Error
            ? query.error.message
            : t('projectDetail.deliverableFolders.loadFailed')}
        </p>
      )}
      {isOpen && query.data !== undefined && (
        <form
          className="mt-3 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate(selected, {
              onSuccess: () => {
                setSelected([]);
              },
            });
          }}
        >
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {query.data.types.map((type) => (
              <li key={type.id} className="text-sm">
                {type.folderUrl === null ? (
                  <label className="flex items-center gap-2 text-[var(--color-text-primary)]">
                    <Checkbox
                      checked={selected.includes(type.id)}
                      disabled={mutation.isPending}
                      onChange={(e) => {
                        toggle(type.id, e.target.checked);
                      }}
                    />
                    {type.label}
                  </label>
                ) : (
                  <a
                    href={type.folderUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--color-accent-primary)] hover:underline"
                  >
                    {type.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" type="submit" disabled={selected.length === 0 || mutation.isPending}>
              {mutation.isPending
                ? t('projectDetail.deliverableFolders.creating')
                : t('projectDetail.deliverableFolders.create')}
            </Button>
            {mutation.isError && (
              <span role="alert" className="text-xs text-[var(--color-error)]">
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : t('projectDetail.deliverableFolders.createFailed')}
              </span>
            )}
          </div>
          {notices.length > 0 && (
            <ul role="status" className="space-y-1 text-xs text-[var(--color-warning)]">
              {notices.map((notice) => (
                <li key={notice}>{notice}</li>
              ))}
            </ul>
          )}
        </form>
      )}
    </section>
  );
}
